import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import sanitizeHtml from 'sanitize-html'
import * as Sentry from '@sentry/browser'
import { IUpdate } from '@src/types/featureUpdates'
import featureUpdatesApi, { MAX_UNREAD_COUNT, WHATS_NEW_LIMIT } from '@src/api/featureUpdates'
import { FeatureToggleVariants } from '@src/composables/featureToggles/featureToggle.variants'
import { getWindowLocationObject } from '@src/utilities/iframeDetection'
import { featureToggleList } from '@src/utilities/featureToggle'
import useRealmSettings from '@src/composables/realmSettings'
import { useFeatureToggles } from '@src/composables/featureToggles'
import useUser from '@src/composables/user'
import _get from 'lodash/get'

const processDetails = (details: string): string => sanitizeHtml(details, {
  allowedTags: [ 'img', 'h4', 'p', 'a', 'b', 'br', 'i' ],
  allowedAttributes: {
    img: [ 'src', 'alt', 'title', 'class' ],
    a: [ 'href' ]
  }
})

function isVisible(updateFilter: string | undefined, isEnabled: boolean) {
  return updateFilter === undefined ? true : isEnabled
}

export const useWhatsNewStore = defineStore('whatsNew', () => {
  const { user } = useUser()
  const isLoading = ref(true)
  const isWhatsNewDialogOpen = ref(false)
  const lastSeenUser = ref<string | undefined>(undefined)
  const offset = ref(0)
  const reachedEnd = ref(false)
  const updates = ref<IUpdate[]>([])
  const wasOpenedAutomatically = ref(false)

  const { realmSettings } = useRealmSettings()
  const { releaseToggles, experiments } = useFeatureToggles()

  const filteredUpdates = computed(() => {
    return updates.value.filter((update: IUpdate): boolean => {
      const isRealmSettingEnabled = _get(realmSettings.value, update.realmSetting ?? '', false)
      const isFeatureToggleEnabled = !!update.featureToggleId && featureToggleList(releaseToggles.value).findByName(update.featureToggleId).isEnabled()
      const isExperimentEnabled = !!update.experimentId && featureToggleList(experiments.value).findByName(update.experimentId).getExperimentVariant() === FeatureToggleVariants.enabled

      return isVisible(update.realmSetting, isRealmSettingEnabled)
          && isVisible(update.featureToggleId, isFeatureToggleEnabled)
          && isVisible(update.experimentId, isExperimentEnabled)
    })
  })

  const unread = computed(() => filteredUpdates.value.filter(({ read }: IUpdate) => !read))
  const unreadCount = computed(() => unread.value.length)
  const unreadCountString = computed(() => (unreadCount.value ? (unreadCount.value <= MAX_UNREAD_COUNT ? `${unreadCount.value}` : `${MAX_UNREAD_COUNT}+`) : ''))
  const hasNotBeenSeen = (days: number): boolean => {
    if (!lastSeenUser.value) {
      return false
    }
    const lastSeen = new Date(lastSeenUser.value).getTime()
    const now = Date.now()
    return now - days * 24 * 60 * 60 * 1000 > lastSeen
  }
  const hasUpdates = computed(() => !!(unreadCount.value && hasNotBeenSeen(14) && getWindowLocationObject().pathname !== '/externalapp/'))

  async function loadFeatureUpdates() {
    try {
      isLoading.value = true
      const { event_tracking_id, language } = user.value
      const { data: { updates: fetchedUpdates, lastSeenUser: fetchedLastSeenUser } } = await featureUpdatesApi.getFeatures(event_tracking_id, offset.value, language)
      offset.value += WHATS_NEW_LIMIT
      setFeatureUpdates(fetchedUpdates)
      lastSeenUser.value = (fetchedLastSeenUser)
    } catch (error) {
      Sentry.captureException(new Error(`loading of what's new failed: ${error}`), (scope: any) => {
        scope.setTag('whats_new', true)
        return scope
      })
    } finally {
      isLoading.value = false
    }
  }

  async function loadMore() {
    try {
      if (isLoading.value || reachedEnd.value) {
        return
      }
      isLoading.value = true
      const { event_tracking_id, language } = user.value
      const { data: { updates: fetchedUpdates } } = await featureUpdatesApi.getFeatures(event_tracking_id, offset.value, language)

      if (fetchedUpdates.length) {
        offset.value += WHATS_NEW_LIMIT
        setFeatureUpdates(fetchedUpdates)
      } else {
        reachedEnd.value = true
      }
    } catch (error) {
      Sentry.captureException(new Error(`loading more items in what's new failed: ${error}`), (scope: any) => {
        scope.setTag('whats_new', true)
        return scope
      })
    } finally {
      isLoading.value = false
    }
  }

  async function openWhatsNewDialog(openOnlyIfUpdates?: boolean) {
    try {
      await loadFeatureUpdates()
      await markAllAsReadForNewUsers()
      if (openOnlyIfUpdates ? hasUpdates.value : true) {
        isWhatsNewDialogOpen.value = true
        wasOpenedAutomatically.value = !!openOnlyIfUpdates
      }
    } catch (error) {
      Sentry.captureException(new Error(`auto opening evaluation of what's new failed: ${error}`), (scope: any) => {
        scope.setTag('whats_new', true)
        return scope
      })
    }
  }

  async function closeWhatsNewDialog() {
    isWhatsNewDialogOpen.value = false
  }

  async function markAsRead() {
    const { event_tracking_id } = user.value
    await featureUpdatesApi.markAsRead(event_tracking_id, unread.value.map((update: IUpdate) => update.id))
    markAllAsRead()
  }

  async function markAllAsReadForNewUsers() {
    if (!lastSeenUser.value) {
      const { event_tracking_id } = user.value
      await featureUpdatesApi.markAsRead(event_tracking_id, [])
      markAllAsRead()
    }
  }

  function setFeatureUpdates(fetchedUpdates: IUpdate[]) {
    updates.value = [
      ...updates.value,
      ...fetchedUpdates.map((update) => ({
        ...update,
        details: processDetails(update.details)
      }))
    ]
  }

  function markAllAsRead() {
    updates.value = updates.value.map((update: IUpdate) => ({ ...update, read: true }))
  }

  return {
    isLoading,
    isWhatsNewDialogOpen,
    lastSeenUser,
    offset,
    reachedEnd,
    updates,
    wasOpenedAutomatically,
    filteredUpdates,
    unread,
    unreadCount,
    unreadCountString,
    hasNotBeenSeen,
    hasUpdates,
    loadMore,
    openWhatsNewDialog,
    closeWhatsNewDialog,
    loadFeatureUpdates,
    markAsRead
  }
})
